import { useCallback, useEffect, useRef, useState } from 'react';
import { playCue, unlockCues, vibrate } from '@/lib/cues.js';
import {
  AUTO_START_DEFAULTS,
  autoStartStep,
  initialAutoStartState,
  type AutoStartConfig,
  type AutoStartState as MachineState,
} from './autoStartMachine.js';

/**
 * Hands-free session start.
 *
 * The real workflow: you press a button, walk back to your desk, and sit down.
 * You cannot see the screen from there, so the app decides on its own when the
 * shot is good and tells you with sound.
 *
 * All timing rules live in `autoStartMachine.ts` as a pure reducer; this hook
 * only drives it on an interval and turns its output into cues and haptics.
 */
export interface AutoStartOptions extends Partial<AutoStartConfig> {
  /** Every quality-gate check currently passes. */
  conditionsMet: boolean;
  /** Called once, after the hold completes. */
  onStart(): void;
}

export interface AutoStartResult {
  armed: boolean;
  progress: number;
  secondsLeft: number | null;
  /** False when this browser refused to give us audio. */
  audioReady: boolean;
  arm(): Promise<void>;
  disarm(): void;
}

export function useAutoStart({
  conditionsMet,
  onStart,
  ...overrides
}: AutoStartOptions): AutoStartResult {
  const [armed, setArmed] = useState(false);
  const [progress, setProgress] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [audioReady, setAudioReady] = useState(false);

  const config: AutoStartConfig = { ...AUTO_START_DEFAULTS, ...overrides };
  const configRef = useRef(config);
  configRef.current = config;

  const metRef = useRef(conditionsMet);
  metRef.current = conditionsMet;
  const onStartRef = useRef(onStart);
  onStartRef.current = onStart;
  const machine = useRef<MachineState>(initialAutoStartState());

  const arm = useCallback(async () => {
    // Must run inside the tap: iOS only unlocks audio from a user gesture.
    const ok = await unlockCues();
    setAudioReady(ok);
    machine.current = initialAutoStartState();
    setProgress(0);
    setSecondsLeft(null);
    setArmed(true);
    playCue('armed');
    vibrate(40);
  }, []);

  const disarm = useCallback(() => {
    setArmed(false);
    machine.current = initialAutoStartState();
    setProgress(0);
    setSecondsLeft(null);
  }, []);

  useEffect(() => {
    if (!armed) return;
    const interval = window.setInterval(() => {
      const step = autoStartStep(
        machine.current,
        { now: Date.now(), conditionsMet: metRef.current },
        configRef.current,
      );
      machine.current = step.state;
      for (const cue of step.cues) playCue(cue);
      setProgress(step.progress);
      setSecondsLeft(step.secondsLeft);
      if (step.start) {
        vibrate([60, 60, 120]);
        setArmed(false);
        onStartRef.current();
      }
    }, 200);
    return () => window.clearInterval(interval);
  }, [armed]);

  return { armed, progress, secondsLeft, audioReady, arm, disarm };
}
